import { useCallback, useState } from 'react';
import { toApiError } from '../../api/errors';
import { EmptyState } from '../../components/ui/EmptyState';
import { ErrorNotice, Notice, StaleBanner } from '../../components/ui/Notice';
import { SkeletonBar } from '../../components/ui/Skeleton';
import { useResource } from '../../hooks/useResource';
import { useProjects } from '../../projects/ProjectsProvider';
import type { MaybeMissing } from '../../api/route-missing';
import type { Id } from '../../api/types';
import {
  createModule,
  fetchModules,
  modulesPath,
  refusalReason,
  updateModule,
  writeRouteMissing,
  type ModuleRow,
} from './settings-api';
import { suggestCode } from './AddRowForm';
import { canWrite, disabledReason, type WriteGate } from './WriteGate';

/**
 * MODULES — the one vocabulary that is PER PROJECT.
 *
 * 🔴 `module_def` is keyed by `project_id` (db/004: *"a module list is meaningless across
 * projects"*), so it is not a `/settings/{collection}` at all — it lives at
 * `/projects/{id}/modules`. That endpoint is NOT DEPLOYED at the reconciled commit.
 *
 * ⛔⛔ SO THIS PANEL DOES NOT INVENT ONE. It names the exact path, says the API answered
 * `no such route`, and shows nothing else. ⚠️ It specifically does not fall back to the module NAMES
 * carried on tickets: those are free text from db/001 with no `is_active` and no ordering, so a list
 * built from them would look like the module list, be missing every unused module, and have no way to
 * say so.
 */
export function ModulesPanel({ gate }: { gate: WriteGate }) {
  const projects = useProjects();
  const [projectId, setProjectId] = useState<Id | ''>('');

  const modules = useResource<MaybeMissing<ModuleRow[]>>(
    (signal) => fetchModules(projectId as Id, signal),
    [projectId],
    { enabled: Boolean(projectId) },
  );

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [label, setLabel] = useState('');
  const editable = canWrite(gate);

  const runWrite = useCallback(
    async (work: () => Promise<void>) => {
      setBusy(true);
      setError(null);
      try {
        await work();
        modules.reload();
      } catch (err) {
        setError(
          refusalReason(err) ??
            (writeRouteMissing(err)
              ? `The API answered "no such route" — ${modulesPath(String(projectId))} is not deployed.`
              : toApiError(err, modulesPath(String(projectId))).message),
        );
      } finally {
        setBusy(false);
      }
    },
    [modules, projectId],
  );

  const result = modules.data;
  const rows = result?.kind === 'ok' ? result.value : [];

  return (
    <section aria-label="Modules" className="min-w-0">
      <header className="px-3 py-3 sm:px-4">
        <h2 className="text-base font-semibold">Modules</h2>
        <p className="mt-0.5 text-sm text-[color:var(--color-ink-muted)]">
          Per project — a module list is meaningless across projects, so these live at{' '}
          <code>/api/v1/projects/{'{id}'}/modules</code> rather than under <code>/settings</code>.
        </p>
      </header>

      <div className="space-y-3 px-3 pb-3 sm:px-4">
        <div>
          <label className="block text-xs font-bold tracking-wide uppercase" htmlFor="modules-project">
            Project
          </label>
          <select
            id="modules-project"
            value={projectId}
            onChange={(event) => setProjectId(event.target.value)}
            className="mt-1 w-full rounded border border-[color:var(--color-line)] bg-[color:var(--color-surface)] px-2 py-2 text-sm sm:max-w-md"
          >
            <option value="">Choose a project…</option>
            {(projects.data ?? []).map((project) => (
              <option key={project.id} value={project.id}>
                {project.key} — {project.name}
              </option>
            ))}
          </select>
        </div>

        {projects.error ? <ErrorNotice error={projects.error} onRetry={projects.reload} /> : null}
        {modules.error && !modules.isStale ? (
          <ErrorNotice error={modules.error} onRetry={modules.reload} />
        ) : null}
        {modules.isStale && modules.error ? (
          <StaleBanner error={modules.error} loadedAt={modules.loadedAt} onRetry={modules.reload} />
        ) : null}

        {result?.kind === 'endpoint-missing' ? (
          <Notice tone="error" title="The modules endpoint does not exist yet">
            <p>
              <code>{result.path}</code> is not deployed — the API answered <code>no such route</code>
              , which is its own 404 handler and not "this project has no modules".
            </p>
            <p className="mt-1">
              ⛔ Nothing is listed below. The module names that appear on tickets are free text from
              db/001 with no active flag and no ordering, so a list built from them would be missing
              every unused module and unable to say so.
            </p>
          </Notice>
        ) : null}

        {error ? (
          <p role="alert" className="text-sm font-semibold text-red-700 dark:text-red-300">
            {error}
          </p>
        ) : null}
      </div>

      {!projectId ? (
        <EmptyState
          title="Choose a project"
          detail="Modules belong to one project — `module_def` has a project_id and codes are unique only within one."
        />
      ) : null}

      {projectId && modules.isLoading ? (
        <div role="status" aria-label="Loading modules" className="space-y-2 px-3 sm:px-4">
          {Array.from({ length: 4 }, (_, index) => (
            <SkeletonBar key={index} className="h-9 w-full" />
          ))}
        </div>
      ) : null}

      {result?.kind === 'ok' && rows.length === 0 ? (
        <EmptyState
          title="This project has no modules yet"
          detail="The request succeeded and returned none — an empty list, not a failure."
        />
      ) : null}

      {rows.length > 0 ? (
        <ul className="border-y border-[color:var(--color-line)]">
          {rows.map((row) => (
            <li
              key={row.id}
              className="flex items-center gap-3 border-b border-[color:var(--color-line)] px-3 py-3 last:border-b-0 sm:px-4"
            >
              <span className="min-w-0 flex-1 text-sm">{row.label}</span>
              {/* 🔴 Code as text, never an input — same rule as every other vocabulary. */}
              <code className="shrink-0 text-xs text-[color:var(--color-ink-muted)]">
                <span aria-hidden="true">🔒 </span>
                {row.code}
              </code>
              <button
                type="button"
                role="switch"
                aria-checked={row.is_active}
                disabled={!editable || busy}
                title={disabledReason(gate)}
                onClick={() =>
                  void runWrite(() =>
                    updateModule(projectId as Id, row.id, { is_active: !row.is_active }),
                  )
                }
                className={`h-8 shrink-0 rounded-full px-3 text-xs font-semibold disabled:opacity-50 ${
                  row.is_active
                    ? 'bg-[color:var(--color-ink)] text-[color:var(--color-surface)]'
                    : 'border border-dashed border-[color:var(--color-line)] text-[color:var(--color-ink-muted)]'
                }`}
              >
                {row.is_active ? 'Active' : 'Inactive'}
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {result?.kind === 'ok' ? (
        <form
          className="flex flex-wrap items-end gap-2 border-t border-[color:var(--color-line)] px-3 py-3 sm:px-4"
          onSubmit={(event) => {
            event.preventDefault();
            const code = suggestCode(label);
            if (!code || !editable) return;
            void runWrite(async () => {
              await createModule(projectId as Id, { code, label: label.trim() });
              setLabel('');
            });
          }}
        >
          <div>
            <label className="block text-xs font-semibold" htmlFor="new-module">
              New module
            </label>
            <input
              id="new-module"
              value={label}
              disabled={!editable || busy}
              onChange={(event) => setLabel(event.target.value)}
              className="mt-1 rounded border border-[color:var(--color-line)] bg-[color:var(--color-surface)] px-2 py-1.5 text-sm"
            />
            <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
              Code <code>{suggestCode(label) || '…'}</code> — permanent once created.
            </p>
          </div>
          <button
            type="submit"
            disabled={!editable || busy || !label.trim()}
            title={disabledReason(gate)}
            className="h-9 rounded bg-[color:var(--color-accent)] px-3 text-sm font-semibold text-[color:var(--color-on-accent)] disabled:opacity-50"
          >
            Add module
          </button>
        </form>
      ) : null}
    </section>
  );
}
